import { useEffect, useRef, useState, useMemo} from "react";
import { trackEvent } from "@/lib/analytics";
import { useQuery, useQueries } from "@tanstack/react-query";
import { Link, useLocation, useSearch } from "wouter";
import Header from "@/components/Header";
import { ProductCard } from "@/components/ProductCard";
import { formatDistanceLabel, getDistanceMiles, reverseGeocodeCity, type ResolvedLocation } from "@/lib/geo";
import { stripDemoMarker } from "@/lib/demo";
import { ShoppingCart } from "@/components/ShoppingCart";
import { useCart } from "@/hooks/useCart";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Heart, ListFilter, Loader2, Search, Sprout, Store, Truck, ShoppingBasket, ChefHat, Sparkles, Landmark, MapPin, X } from "lucide-react";
import type { Item, User, CartItemWithDetails } from "@shared/schema";
import { hasListingPhoto } from "@shared/schema";
import { getListingImages } from "@shared/clothing";
import { ListingPhotoPlaceholder } from "@/components/ListingPhotoPlaceholder";
import { formatWeight, unitDisplayLabel } from "@shared/weight";
import { isSnapEligibleItem, isEbtVisible } from "@shared/snap";
import { DISTANCE_OPTIONS, BROWSE_FILTERS_EVENT, type BrowseFilters } from "@/lib/browseFilters";
import { useSavedItems } from "@/hooks/useSavedItems";
import { useFollowing } from "@/hooks/useFollowing";
import { CountdownBanner } from "@/components/CountdownBanner";
import { InviteNudgeBanner } from "@/components/InviteNudgeBanner";
import { ListFirstProductBanner } from "@/components/ListFirstProductBanner";
import { CommunityChampions } from "@/components/CommunityChampions";
import { CategoryChips, BROWSE_CATEGORIES } from "@/components/CategoryChips";
import { RequestItemCard } from "@/components/RequestItemCard";
import { buildProducerIndex, matchesItem, matchesProducer, matchesBox, matchesAssist, type SearchAssist } from "@shared/searchFields";
import { Capacitor } from "@capacitor/core";

type BuyerFulfillmentOption = {
  id: string;
  fulfillmentMethod: "seller_pickup" | "drop_zone_pickup" | "local_delivery";
  buyerLabel: string;
  description?: string | null;
  dayLabel?: string | null;
  timeSlots?: {
    id: string;
    dayOfWeek: string;
    startTime: string;
    endTime: string;
  }[];
  dropZone?: {
    id: string;
    name: string;
    city: string;
    state: string;
    zipCode: string;
    pickupWindowLabel?: string | null;
  } | null;
};

type FulfillmentFilter = "any" | "seller_pickup" | "drop_zone_pickup" | "local_delivery";
type SortOption = "default" | "nearest" | "price_low" | "price_high";
type ProducerTypeFilter = "any" | "grower" | "maker" | "farmstand" | "mixed";
type ProducerSortOption = "default" | "nearest" | "name_az";

type RecommendedBox = {
  id: string;
  boxType: "single_seller" | "nearby_pair";
  title: string;
  sellerNames: string[];
  sellerIds: string[];
  itemCount: number;
  items: {
    id: string;
    name: string;
    price: string;
    sellerId: string;
  }[];
  subtotal: number;
  deliveryAvailable: boolean;
  pickupAvailable: boolean;
  deliveryMinimum: number | null;
  deliveryFee: number | null;
  distanceMiles: number | null;
};

type BrowseProducer = {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  displayName?: string | null;
  bio?: string | null;
  profileImageUrl?: string | null;
  coverImageUrl?: string | null;
  sellerType?: string | null;
  roles?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  latitude?: string | number | null;
  longitude?: string | number | null;
  geocodeSource?: string | null;
  zipCode?: string | null;
  canBuy?: boolean;
  canSell?: boolean;
  featuredUntil?: string | null;
  averageRating?: number;
  reviewCount?: number;
};

function prettyLabel(value?: string | null) {
  if (!value) return null;
  if (value === "farmstand") return "Farm Stand";
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function formatAvailabilityLabel(option?: BuyerFulfillmentOption | null) {
  if (!option) return null;

  const primary =
    option.fulfillmentMethod === "seller_pickup"
      ? "Pickup"
      : option.fulfillmentMethod === "drop_zone_pickup"
        ? "Hub"
        : option.fulfillmentMethod === "local_delivery"
          ? "Delivery"
          : null;

  const day =
    option.dayLabel?.trim() ||
    (option.timeSlots && option.timeSlots.length > 0
      ? `${option.timeSlots[0].dayOfWeek.slice(0, 3)} ${option.timeSlots[0].startTime}`
      : null);

  if (primary && day) return `${primary} ${day.replace(/\s+/g, "")}`;
  if (primary) return primary;
  return option.buyerLabel?.trim() || null;
}

function getResolvedProducerLocation(producer: BrowseProducer): ResolvedLocation | null {
  const parsedLatitude =
    producer.latitude == null || producer.latitude === ""
      ? null
      : Number(producer.latitude);
  const parsedLongitude =
    producer.longitude == null || producer.longitude === ""
      ? null
      : Number(producer.longitude);

  if (!Number.isFinite(parsedLatitude) || !Number.isFinite(parsedLongitude)) {
    return null;
  }

  return {
    source: "producer_address",
    label: producer.displayName ?? null,
    zipCode: producer.zipCode ?? null,
    point: {
      latitude: parsedLatitude as number,
      longitude: parsedLongitude as number,
    },
  };
}

type BrowseMode = "boxes" | "products" | "producers" | "following" | "saved";

interface HomeProps {
  /** Rendered inside a SwipeTabs panel locked to one mode: skip own
   *  Header/pills/search/cart chrome, and take search/zip/location from the
   *  shared wrapper. The wrapper owns mode/q/zip in the URL; the active panel
   *  still owns its own browse filters there, since the map reads them from and
   *  writes them back to the URL (see browseFilters.ts). */
  embedded?: boolean;
  lockedMode?: BrowseMode;
  /** Only the visible panel syncs its filters to the URL — the others share it
   *  and would clobber each other. */
  isActive?: boolean;
  searchQuery?: string;
  onSearchQueryChange?: (v: string) => void;
  /** AI-expanded vocabulary for the current query (server/search-assist.ts).
   *  Travels with the query so a broadened match means the same thing here as
   *  it does in the typeahead — otherwise the dropdown offers results the tab
   *  can't find, which is the exact bug the shared matcher exists to prevent. */
  assist?: SearchAssist | null;
  zipCode?: string;
  buyerLocation?: ResolvedLocation | null;
  onModeChange?: (mode: BrowseMode) => void;
  /** A category the feed asked us to preselect on the way to this tab. Carries a
   *  sequence number so tapping the same chip twice still applies, and so it
   *  still lands on a panel that only mounts once the swipe reaches it. */
  categoryRequest?: { value: string | null; seq: number };
  /** Offset the filter row pins at, so it comes to rest under the wrapper's
   *  pill bar instead of behind it. */
  stickyTop?: number;
}

export default function Home(props: HomeProps = {}) {
  const embedded = props.embedded ?? false;
  const isActivePanel = props.isActive ?? false;
  const [location, setLocation] = useLocation();
  const currentSearch = typeof window !== "undefined" ? window.location.search : "";
  const search = useSearch();
  // Embedded panels re-assert their filters when a navigation rewrites the URL;
  // standalone Home must not, because its own read effect already keys on
  // `search` and the two would trade writes forever (wouter notifies useSearch
  // on replaceState as well as popstate).
  const searchForWrite = embedded ? search : "";
  const { toast } = useToast();
  const { user } = useAuth();
  const { cart, addToCart, updateQuantity, removeFromCart, clearCart, cartCount } = useCart();

  const initialParams =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams();

  const initialModeParam = initialParams.get("mode") ?? initialParams.get("browse");
  const initialBrowseMode =
    initialModeParam === "following"
      ? "following"
      : initialModeParam === "producers"
        ? "producers"
        : initialModeParam === "products"
          ? "products"
          : initialModeParam === "saved"
            ? "saved"
            : "boxes";

  const [isCartOpen, setIsCartOpen] = useState(false);
  const initialFulfillmentFilter = (() => {
    const value = initialParams.get("fulfillment");
    return value === "seller_pickup" || value === "drop_zone_pickup" || value === "local_delivery"
      ? value
      : "any";
  })();

  const [localSearchQuery, setLocalSearchQuery] = useState(initialParams.get("q") ?? "");
  const [localZipCode, setLocalZipCode] = useState(initialParams.get("zip") ?? "");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(initialParams.get("category"));
  const [fulfillmentFilter, setFulfillmentFilter] = useState<FulfillmentFilter>(initialFulfillmentFilter);
  const [sortOption, setSortOption] = useState<SortOption>("default");
  const [showSoldOut, setShowSoldOut] = useState(true);
  const [ebtOnly, setEbtOnly] = useState(initialParams.get("ebt") === "1");
  const [withinMiles, setWithinMiles] = useState<number | null>(() => {
    const n = Number(initialParams.get("within"));
    return Number.isFinite(n) && n > 0 ? n : null;
  });
  const [producerTypeFilter, setProducerTypeFilter] = useState<ProducerTypeFilter>("any");
  const [producerSortOption, setProducerSortOption] = useState<ProducerSortOption>("default");
  // Category, fulfillment, distance and sold-out are one selection shared by
  // Listings, Growers & Makers and the map, so crossing between them keeps what
  // you picked. Every panel holds its own copy of the state and they are kept in
  // step by BROWSE_FILTERS_EVENT — the same channel the map already uses.
  // Sort stays per tab (price is meaningless for makers) and Type is makers-only.
  // Read at call time, not render time: the search query is declared further
  // down (it can come from props), so capturing it here would be a TDZ error.
  const sharedFilters = (): BrowseFilters => ({
    category: selectedCategory,
    fulfillment: fulfillmentFilter,
    showSoldOut,
    withinMiles,
    ebtOnly,
    q: searchQuery.trim(),
  });
  // Only a user action broadcasts; applying a received change never re-emits, so
  // panels can't drive each other in a loop.
  const broadcastFilters = (patch: Partial<BrowseFilters>) => {
    window.dispatchEvent(
      new CustomEvent(BROWSE_FILTERS_EVENT, { detail: { ...sharedFilters(), ...patch } }),
    );
  };
  const setSharedCategory = (value: string | null) => {
    setSelectedCategory(value);
    broadcastFilters({ category: value });
  };
  const setSharedFulfillment = (value: FulfillmentFilter) => {
    setFulfillmentFilter(value);
    broadcastFilters({ fulfillment: value });
  };
  const setSharedWithinMiles = (value: number | null) => {
    setWithinMiles(value);
    broadcastFilters({ withinMiles: value });
  };
  const setSharedShowSoldOut = (value: boolean) => {
    setShowSoldOut(value);
    broadcastFilters({ showSoldOut: value });
  };
  const activeSharedFilterCount =
    (selectedCategory ? 1 : 0) +
    (fulfillmentFilter !== "any" ? 1 : 0) +
    (withinMiles != null ? 1 : 0) +
    (showSoldOut ? 0 : 1) +
    (ebtOnly ? 1 : 0);
  const clearSharedFilters = () => {
    setSelectedCategory(null);
    setFulfillmentFilter("any");
    setWithinMiles(null);
    setShowSoldOut(true);
    setEbtOnly(false);
    broadcastFilters({
      category: null,
      fulfillment: "any",
      withinMiles: null,
      showSoldOut: true,
      ebtOnly: false,
    });
  };
  const [boxesDeliveryOnly, setBoxesDeliveryOnly] = useState(false);
  const [selectedBox, setSelectedBox] = useState<RecommendedBox | null>(null);
  const [localBrowseMode, setLocalBrowseMode] = useState<BrowseMode>(initialBrowseMode);
  const [localBuyerLocation, setLocalBuyerLocation] = useState<ResolvedLocation | null>(() => {
    if (typeof window === "undefined") return null;
    try {
      const raw = window.localStorage.getItem("marketlink_buyer_location");
      return raw ? (JSON.parse(raw) as ResolvedLocation) : null;
    } catch {
      return null;
    }
  });
  const [isLocatingBuyer, setIsLocatingBuyer] = useState(false);
  const [buyerLocationError, setBuyerLocationError] = useState<string | null>(null);

  // Controlled-or-uncontrolled. Embedded in the swipe wrapper, search/zip/
  // location come from the shared parent so every panel filters and sorts
  // against the same values; standalone they stay local to this component.
  const searchQuery = embedded ? props.searchQuery ?? "" : localSearchQuery;
  const setSearchQuery: (v: string) => void = embedded
    ? props.onSearchQueryChange ?? (() => {})
    : setLocalSearchQuery;
  const zipCode = embedded ? props.zipCode ?? "" : localZipCode;
  const setZipCode = setLocalZipCode;
  const buyerLocation = embedded ? props.buyerLocation ?? null : localBuyerLocation;
  const setBuyerLocation = setLocalBuyerLocation;
  const browseMode: BrowseMode = embedded ? props.lockedMode ?? "boxes" : localBrowseMode;
  const goMode = (mode: BrowseMode) => {
    if (embedded) props.onModeChange?.(mode);
    else setLocalBrowseMode(mode);
  };

  useEffect(() => {
    if (embedded) return;
    if (!zipCode && user?.zipCode) {
      setZipCode(user.zipCode);
    }
  }, [embedded, user?.zipCode, zipCode]);

  useEffect(() => {
    function emit() {
      if (sortOption === "nearest" || producerSortOption === "nearest") {
        window.dispatchEvent(new CustomEvent("mission:sort-distance"));
      }
    }
    emit();
    function onRecheck(ev: Event) {
      const detail = (ev as CustomEvent).detail as { event?: string } | undefined;
      if (!detail || detail.event === "mission:sort-distance") emit();
    }
    window.addEventListener("marketlink:mission-recheck", onRecheck);
    return () => window.removeEventListener("marketlink:mission-recheck", onRecheck);
  }, [sortOption, producerSortOption]);

  useEffect(() => {
    function emit() {
      if (fulfillmentFilter !== "any") {
        window.dispatchEvent(new CustomEvent("mission:filter-fulfillment"));
      }
    }
    emit();
    function onRecheck(ev: Event) {
      const detail = (ev as CustomEvent).detail as { event?: string } | undefined;
      if (!detail || detail.event === "mission:filter-fulfillment") emit();
    }
    window.addEventListener("marketlink:mission-recheck", onRecheck);
    return () => window.removeEventListener("marketlink:mission-recheck", onRecheck);
  }, [fulfillmentFilter]);

  useEffect(() => {
    if (embedded) return;
    if (typeof window === "undefined") return;

    if (!buyerLocation) {
      window.localStorage.removeItem("marketlink_buyer_location");
      return;
    }

    window.localStorage.setItem("marketlink_buyer_location", JSON.stringify(buyerLocation));
  }, [embedded, buyerLocation]);

  useEffect(() => {
    if (embedded) return;
    if (typeof window === "undefined") return;
    if (buyerLocation?.source === "browser_gps") return;

    let cancelled = false;
    setIsLocatingBuyer(true);
    setBuyerLocationError(null);

    const onSuccess = (latitude: number, longitude: number) => {
      if (cancelled) return;
      setBuyerLocation({
        source: "browser_gps",
        label: null,
        zipCode: zipCode?.trim() || user?.zipCode || null,
        point: { latitude, longitude },
      });
      setIsLocatingBuyer(false);
      reverseGeocodeCity(latitude, longitude).then((city) => {
        if (cancelled || !city) return;
        setBuyerLocation((prev) =>
          prev && prev.source === "browser_gps" ? { ...prev, label: city } : prev,
        );
      });
    };

    const onFail = () => {
      if (cancelled) return;
      setBuyerLocationError(null);
      setIsLocatingBuyer(false);
    };

    if (Capacitor.isNativePlatform()) {
      import("@capacitor/geolocation").then(({ Geolocation }) =>
        Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 8000 })
          .then((pos) => onSuccess(pos.coords.latitude, pos.coords.longitude))
          .catch(onFail)
      ).catch(onFail);
    } else if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (pos) => onSuccess(pos.coords.latitude, pos.coords.longitude),
        onFail,
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 300000 }
      );
    } else {
      onFail();
    }

    return () => {
      cancelled = true;
    };
  }, [embedded, buyerLocation?.source, user?.zipCode, zipCode]);

  useEffect(() => {
    if (embedded) return;
    const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
    const modeParam = params.get("mode") ?? params.get("browse");
    const nextBrowseMode =
      modeParam === "following"
        ? "following"
        : modeParam === "producers"
          ? "producers"
          : modeParam === "products"
            ? "products"
            : modeParam === "saved"
              ? "saved"
              : "boxes";
    const nextSearchQuery = params.get("q") ?? "";
    const nextZipCode = params.get("zip") ?? "";
    const nextCategory = nextBrowseMode === "products" ? params.get("category") : null;
    const nextFulfillment = nextBrowseMode === "products" ? params.get("fulfillment") : null;
    const nextSort = nextBrowseMode === "products" ? params.get("sort") : null;
    const nextShowSoldOut = nextBrowseMode === "products" ? params.get("showSoldOut") : null;
    const normalizedFulfillment: FulfillmentFilter =
      nextFulfillment === "seller_pickup" ||
      nextFulfillment === "drop_zone_pickup" ||
      nextFulfillment === "local_delivery"
        ? nextFulfillment
        : "any";
    const normalizedSort: SortOption =
      nextSort === "nearest" ||
      nextSort === "price_low" ||
      nextSort === "price_high"
        ? nextSort
        : "default";
    const normalizedShowSoldOut = nextShowSoldOut === "0" ? false : true;
    // Distance is written for every mode (the map reads it from any tab), so
    // read it back for every mode too — not gated on products like the rest.
    const withinParam = Number(params.get("within"));
    const nextWithin = Number.isFinite(withinParam) && withinParam > 0 ? withinParam : null;

    setLocalBrowseMode((current) => (current === nextBrowseMode ? current : nextBrowseMode));
    setLocalSearchQuery((current) => (current === nextSearchQuery ? current : nextSearchQuery));
    setLocalZipCode((current) => (current === nextZipCode ? current : nextZipCode));
    setSelectedCategory((current) => (current === nextCategory ? current : nextCategory));
    setFulfillmentFilter((current) => (current === normalizedFulfillment ? current : normalizedFulfillment));
    setSortOption((current) => (current === normalizedSort ? current : normalizedSort));
    setShowSoldOut((current) => (current === normalizedShowSoldOut ? current : normalizedShowSoldOut));
    setWithinMiles((current) => (current === nextWithin ? current : nextWithin));
  }, [embedded, search]);

  // Filters changed on the map. Distance is shared by every mode; the rest are
  // mode-specific, and in producers mode the map's fulfillment drives the
  // producer filter rather than the product one.
  // Every panel listens, not just the visible one — that is what makes the
  // selection persist when you swipe between Listings and Growers & Makers, and
  // what lets a filter set on the map be there on whichever tab you return to.
  useEffect(() => {
    const onSharedFilters = (event: Event) => {
      const next = (event as CustomEvent<BrowseFilters>).detail;
      if (!next) return;
      setWithinMiles((c) => (c === next.withinMiles ? c : next.withinMiles));
      setSelectedCategory((c) => (c === next.category ? c : next.category));
      setFulfillmentFilter((c) => (c === next.fulfillment ? c : next.fulfillment));
      setShowSoldOut((c) => (c === next.showSoldOut ? c : next.showSoldOut));
      setEbtOnly((c) => (c === next.ebtOnly ? c : next.ebtOnly));
      // Clearing the search on the map has to reach the list too, or closing the
      // map drops you back onto a still-filtered tab with an empty search box.
      if (next.q !== undefined) setSearchQuery(next.q);
    };
    window.addEventListener(BROWSE_FILTERS_EVENT, onSharedFilters);
    return () => window.removeEventListener(BROWSE_FILTERS_EVENT, onSharedFilters);
  }, []);

  // Category handed over by the feed. Tracked by sequence rather than value so a
  // repeat tap re-applies, and so a panel that mounts later still picks up a
  // request made while it did not exist.
  const categoryRequestSeqRef = useRef(0);
  useEffect(() => {
    const request = props.categoryRequest;
    if (!request || request.seq === categoryRequestSeqRef.current) return;
    categoryRequestSeqRef.current = request.seq;
    if (browseMode !== "products") return;
    // Broadcast rather than set locally. Tapping a category is a user action
    // wherever it happens, and in the pager the event is the ONLY way the other
    // panels hear about it — the URL reader above returns early when embedded.
    // Setting state directly here meant a category chosen on the feed applied
    // to Listings and to the URL, but Growers & Makers and the map never saw
    // it, so the filter looked like it stopped carrying over depending on
    // which screen you picked it from.
    setSharedCategory(request.value);
  }, [props.categoryRequest, browseMode]);

  useEffect(() => {
    if (embedded && !isActivePanel) return;
    if (typeof window === "undefined") return;
    void searchForWrite;

    // Standalone, Home owns the whole query string, so it builds it from
    // scratch. Embedded, the wrapper owns mode/q/zip and the map may own
    // filters it wrote, so merge into the live params and only touch our keys.
    const params = new URLSearchParams(embedded ? window.location.search : "");
    const setOrDelete = (key: string, value: string | null) => {
      if (value == null) params.delete(key);
      else params.set(key, value);
    };

    if (!embedded) {
      params.set("mode", browseMode);
      setOrDelete("q", searchQuery.trim() || null);
      setOrDelete("zip", zipCode.trim() || null);
    }

    // These four are one selection shared by every tab, so whichever panel is
    // visible writes them — this effect has already returned unless we are it.
    // Gating this on the products view (a leftover from when each tab held its
    // own filters) meant a category picked on Growers & Makers never reached the
    // URL at all, and the map seeds from the URL at mount, so it opened
    // unfiltered until you happened to visit Listings and let it write the key.
    const products = browseMode === "products";
    setOrDelete("category", selectedCategory);
    setOrDelete("fulfillment", fulfillmentFilter !== "any" ? fulfillmentFilter : null);
    setOrDelete("showSoldOut", showSoldOut ? null : "0");
    setOrDelete("ebt", ebtOnly ? "1" : null);

    // Sort is per tab, not shared — price sorts are meaningless for makers — so
    // only the products view manages that key.
    if (products || !embedded) {
      setOrDelete("sort", products && sortOption !== "default" ? sortOption : null);
    }
    setOrDelete("within", withinMiles != null ? String(withinMiles) : null);

    const basePath = embedded ? window.location.pathname : location;
    const query = params.toString();
    const next = query ? `${basePath}?${query}` : basePath;
    const current = `${window.location.pathname}${window.location.search}`;

    if (next !== current) {
      // Keep the existing history state: it carries the entry the map pushed to
      // own its back button, and replacing it with {} strands the user on the
      // page underneath when they back out of the map.
      window.history.replaceState(window.history.state, "", next);
    }
    // `searchForWrite` re-asserts the filters after a navigation replaces the URL
    // underneath us — closing the map pops the entry it pushed, restoring the
    // query string from before its filters existed. It is held at "" when
    // standalone: wouter patches replaceState and notifies useSearch, so a
    // standalone Home (whose read effect also keys on `search`) would trade
    // writes with itself and never settle.
  }, [embedded, isActivePanel, browseMode, searchQuery, zipCode, selectedCategory, fulfillmentFilter, sortOption, showSoldOut, ebtOnly, withinMiles, location, searchForWrite]);

  // Cached app-wide (App.tsx fetches the same key at startup) — cheap feature
  // flag, no extra request in practice.
  const { data: publicConfig } = useQuery<{ snapPlatformApproved?: boolean }>({
    queryKey: ["/api/public/config"],
  });
  const snapPlatformApproved = publicConfig?.snapPlatformApproved ?? false;

  const { data: items = [], isLoading, isError: itemsError, refetch: refetchItems } = useQuery<(Item & { seller: User })[]>({
    // Deliberately not scoped to the buyer's ZIP. `?zip=` is an exact-match
    // filter, so a maker one ZIP over was invisible here while still appearing
    // on the feed, in Growers & Makers and on the map — you could tap a maker on
    // the map and find nothing in Listings. HomeFeed already dropped this for the
    // same reason ("an Albany buyer never saw an Albany maker because their
    // account ZIP differed"); this is the last surface that hadn't. Proximity is
    // expressed by the nearest-first sort and the Distance filter instead. Shares
    // the feed's cache entry, so the two tabs cost one request.
    queryKey: ["/api/items", "all"],
    queryFn: async () => {
      const res = await fetch("/api/items", {
        credentials: "include",
      });

      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
  });

  // A buyer with a ZIP set who sees an empty feed is an underserved ZIP — no
  // makers reach them. Log it so we can see where to recruit (the feed hard-
  // filters to exact-ZIP matches, so this is a real "nothing nearby" signal).
  useEffect(() => {
    if (embedded) return;
    if (!isLoading && zipCode.trim() && items.length === 0) {
      trackEvent("empty_feed_for_zip", "/", { zip: zipCode.trim() });
    }
  }, [embedded, isLoading, items.length, zipCode]);

  const { data: recommendedBoxes = [], isLoading: isLoadingBoxes, isError: boxesError, refetch: refetchBoxes } = useQuery<RecommendedBox[]>({
    queryKey: ["/api/boxes", zipCode, boxesDeliveryOnly],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (zipCode.trim()) params.set("zip", zipCode.trim());
      if (boxesDeliveryOnly) params.set("deliveryAvailable", "true");

      const url = params.toString() ? `/api/boxes?${params.toString()}` : "/api/boxes";
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    enabled: browseMode === "boxes" || browseMode === "products" || browseMode === "following",
  });

  const { data: producers = [], isLoading: isLoadingProducers, isError: producersError, refetch: refetchProducers } = useQuery<BrowseProducer[]>({
    queryKey: ["/api/sellers"],
    queryFn: async () => {
      const res = await fetch("/api/sellers", { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
  });

  const { data: followingFeedItems = [], isLoading: isLoadingFollowing, isError: followingError, refetch: refetchFollowing } = useQuery<(Item & { seller: User })[]>({
    queryKey: ["/api/my/following-feed"],
    queryFn: async () => {
      const res = await fetch("/api/my/following-feed", { credentials: "include" });
      if (res.status === 401) return [];
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    enabled: browseMode === "following",
  });

  const { data: savedFeedItems = [], isLoading: isLoadingSaved, isError: savedError, refetch: refetchSaved } = useQuery<(Item & { seller: User })[]>({
    queryKey: ["/api/my/saved-items"],
    queryFn: async () => {
      const res = await fetch("/api/my/saved-items", { credentials: "include" });
      if (res.status === 401) return [];
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    enabled: browseMode === "saved",
  });

  const { data: forYouItems = [], isLoading: isLoadingForYou } = useQuery<(Item & { seller: User })[]>({
    queryKey: ["/api/my/for-you"],
    queryFn: async () => {
      const res = await fetch("/api/my/for-you", { credentials: "include" });
      if (res.status === 401) return [];
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    enabled: browseMode === "saved",
  });

  const { savedSet, toggleSave } = useSavedItems();
  const { followingSet, toggleFollow } = useFollowing();

  const { data: cartItemsDetails = [] } = useQuery<CartItemWithDetails[]>({
    queryKey: ["/api/cart/details", JSON.stringify(cart)],
    queryFn: async () => {
      if (cart.length === 0) return [];
      const res = await fetch(`/api/cart/details?cart=${encodeURIComponent(JSON.stringify(cart))}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    enabled: cart.length > 0,
  });

  const fulfillmentQueries = useQueries({
    queries: items.map((item) => ({
      queryKey: ["/api/items/fulfillment-options", item.id],
      queryFn: async (): Promise<BuyerFulfillmentOption[]> => {
        const res = await fetch(`/api/items/${item.id}/fulfillment-options`, {
          credentials: "include",
        });
        if (!res.ok) throw new Error(await res.text());
        return res.json();
      },
      enabled: items.length > 0,
    })),
  });

  const fulfillmentOptionsByItemId = new Map(
    items.map((item, index) => [item.id, fulfillmentQueries[index]?.data ?? []] as const)
  );

  const producerById = new Map(producers.map((producer) => [producer.id, producer] as const));

  const producerFulfillmentMethodsById = new Map<string, Set<FulfillmentFilter>>();
  const producerPrimaryOptionById = new Map<string, BuyerFulfillmentOption>();
  // Derived from the item list the same way fulfillment methods are, so a maker
  // answers the shared category and sold-out filters exactly as their listings
  // would. The item list is no longer ZIP-scoped, so this sees every maker.
  const producerCategoriesById = new Map<string, Set<string>>();
  const producerHasStockById = new Map<string, boolean>();

  items.forEach((item) => {
    if (item.category) {
      const cats = producerCategoriesById.get(item.sellerId) ?? new Set<string>();
      cats.add(item.category);
      producerCategoriesById.set(item.sellerId, cats);
    }
    if (item.qtyAvailable > 0) producerHasStockById.set(item.sellerId, true);
  });

  items.forEach((item) => {
    const methods = producerFulfillmentMethodsById.get(item.sellerId) ?? new Set<FulfillmentFilter>();
    const options = fulfillmentOptionsByItemId.get(item.id) ?? [];

    options.forEach((option) => {
      if (
        option.fulfillmentMethod === "seller_pickup" ||
        option.fulfillmentMethod === "drop_zone_pickup" ||
        option.fulfillmentMethod === "local_delivery"
      ) {
        methods.add(option.fulfillmentMethod);
      }
    });

    if (!producerPrimaryOptionById.has(item.sellerId)) {
      const preferredOption =
        options.find((option) => option.fulfillmentMethod === "seller_pickup") ||
        options.find((option) => option.fulfillmentMethod === "drop_zone_pickup") ||
        options.find((option) => option.fulfillmentMethod === "local_delivery");

      if (preferredOption) {
        producerPrimaryOptionById.set(item.sellerId, preferredOption);
      }
    }

    producerFulfillmentMethodsById.set(item.sellerId, methods);
  });

  // A category-eligible item counts once its seller is individually approved,
  // or once the platform-wide switch is on (covers every seller) — see
  // shared/snap.ts. Reused for both the filter itself and deciding whether the
  // EBT pill should even render.
  const itemIsEbtEligible = (item: Item & { seller?: User | null }) =>
    isSnapEligibleItem(item.category, item.subtype) &&
    isEbtVisible(Number((item.seller as any)?.snapApproved ?? 0) === 1, snapPlatformApproved);
  const anyEbtEligibleItems = items.some(itemIsEbtEligible);

  const buyerDistanceFallbackZip = buyerLocation?.zipCode?.trim() || zipCode?.trim() || user?.zipCode || null;

  // Same inputs the "Nearest" sort uses, so the radius filter and the sort can't
  // disagree about how far away something is. Unknown distance sorts as
  // Infinity, which means an item we can't place is excluded by a radius rather
  // than silently included.
  const itemMiles = (item: Item & { seller?: unknown }): number => {
    const seller = (item as Item & { seller?: BrowseProducer }).seller;
    const miles = getDistanceMiles({
      buyerLocation,
      producerLocation: seller ? getResolvedProducerLocation(seller) : null,
      fallbackBuyerZip: buyerDistanceFallbackZip,
      fallbackProducerZip: seller?.zipCode ?? item.zipCode ?? null,
    });
    return typeof miles === "number" && Number.isFinite(miles) ? miles : Infinity;
  };

  // Same matcher the typeahead uses (see lib/searchFields), so a query that
  // offers ten results in the dropdown finds those same ten here.
  const producerIndex = useMemo(() => buildProducerIndex(items as any), [items]);
  const assist = props.assist ?? null;
  const assistSellerIds = useMemo(() => {
    if (!assist) return null;
    const ids = new Set<string>();
    for (const item of items) {
      if (matchesAssist(assist, item as any)) ids.add(item.sellerId);
    }
    return ids;
  }, [assist, items]);
  const matchesQuery = (item: Item & { seller?: User }) =>
    matchesItem(searchQuery, item as any) || (assist ? matchesAssist(assist, item as any) : false);

  const filteredItems = items.filter((item) => {
    const matchesSearch = searchQuery.trim() === "" || matchesQuery(item);
    const matchesCategory = !selectedCategory || item.category === selectedCategory;
    const itemFulfillmentOptions = fulfillmentOptionsByItemId.get(item.id) ?? [];
    const matchesFulfillment =
      fulfillmentFilter === "any"
        ? true
        : itemFulfillmentOptions.some((option) => option.fulfillmentMethod === fulfillmentFilter);
    const matchesSoldOut = showSoldOut ? true : item.qtyAvailable > 0;
    const matchesEbt = ebtOnly ? itemIsEbtEligible(item) : true;
    const matchesDistance = withinMiles == null || itemMiles(item) <= withinMiles;
    return (
      matchesSearch && matchesCategory && matchesFulfillment && matchesSoldOut && matchesEbt && matchesDistance
    );
  });

  const filteredBoxes = recommendedBoxes.filter(
    (box) =>
      searchQuery.trim() === "" ||
      matchesBox(searchQuery, box) ||
      (assist?.terms ?? []).some((term) => matchesBox(term, box)),
  );

  const sortedBoxes = [...filteredBoxes].sort((a, b) => {
    if (sortOption === "price_low") {
      return a.subtotal - b.subtotal;
    }

    if (sortOption === "price_high") {
      return b.subtotal - a.subtotal;
    }

    if (sortOption === "nearest") {
      const aDistances = a.sellerIds
        .map((sellerId) => {
          const producer = producerById.get(sellerId);
          if (!producer) return null;

          const miles = getDistanceMiles({
            buyerLocation,
            producerLocation: getResolvedProducerLocation(producer),
            fallbackBuyerZip: buyerDistanceFallbackZip,
            fallbackProducerZip: producer.zipCode ?? null,
          });

          return typeof miles === "number" && Number.isFinite(miles) ? miles : null;
        })
        .filter((value): value is number => value != null);

      const bDistances = b.sellerIds
        .map((sellerId) => {
          const producer = producerById.get(sellerId);
          if (!producer) return null;

          const miles = getDistanceMiles({
            buyerLocation,
            producerLocation: getResolvedProducerLocation(producer),
            fallbackBuyerZip: buyerDistanceFallbackZip,
            fallbackProducerZip: producer.zipCode ?? null,
          });

          return typeof miles === "number" && Number.isFinite(miles) ? miles : null;
        })
        .filter((value): value is number => value != null);

      const aNearest = aDistances.length ? Math.min(...aDistances) : null;
      const bNearest = bDistances.length ? Math.min(...bDistances) : null;

      if (aNearest == null && bNearest == null) return a.title.localeCompare(b.title);
      if (aNearest == null) return 1;
      if (bNearest == null) return -1;

      return aNearest - bNearest;
    }

    return a.title.localeCompare(b.title);
  });

  const sortedItems = [...filteredItems].sort((a, b) => {
    if (sortOption === "price_low") {
      return Number(a.price) - Number(b.price);
    }

    if (sortOption === "price_high") {
      return Number(b.price) - Number(a.price);
    }

    // Default is nearest-first, not server order. The list is no longer scoped
    // to the buyer's ZIP, so without this the closest makers would be scattered
    // through items from the far side of the region.
    if (sortOption === "nearest" || sortOption === "default") {
      const aSeller = (a as typeof a & { seller?: BrowseProducer }).seller;
      const bSeller = (b as typeof b & { seller?: BrowseProducer }).seller;

      const aDistance = getDistanceMiles({
        buyerLocation,
        producerLocation: aSeller ? getResolvedProducerLocation(aSeller) : null,
        fallbackBuyerZip: buyerDistanceFallbackZip,
        fallbackProducerZip: aSeller?.zipCode ?? a.zipCode ?? null,
      });

      const bDistance = getDistanceMiles({
        buyerLocation,
        producerLocation: bSeller ? getResolvedProducerLocation(bSeller) : null,
        fallbackBuyerZip: buyerDistanceFallbackZip,
        fallbackProducerZip: bSeller?.zipCode ?? b.zipCode ?? null,
      });

      const aHasRealCoords = Boolean(
        aSeller &&
          aSeller.latitude != null &&
          aSeller.latitude !== "" &&
          aSeller.longitude != null &&
          aSeller.longitude !== ""
      );
      const bHasRealCoords = Boolean(
        bSeller &&
          bSeller.latitude != null &&
          bSeller.latitude !== "" &&
          bSeller.longitude != null &&
          bSeller.longitude !== ""
      );

      if (aHasRealCoords && !bHasRealCoords) return -1;
      if (!aHasRealCoords && bHasRealCoords) return 1;

      if (aDistance == null && bDistance == null) return 0;
      if (aDistance == null) return 1;
      if (bDistance == null) return -1;

      return aDistance - bDistance;
    }

    return 0;
  });

  const filteredProducers = producers.filter((producer) => {
    const matchesSearch =
      searchQuery.trim() === "" ||
      matchesProducer(searchQuery, producer as any, producerIndex) ||
      (assistSellerIds?.has(producer.id) ?? false);
    const matchesType =
      producerTypeFilter === "any" ? true : producer.sellerType === producerTypeFilter;

    const producerFulfillmentMethods =
      producerFulfillmentMethodsById.get(producer.id) ?? new Set<FulfillmentFilter>();

    const matchesFulfillment =
      fulfillmentFilter === "any" ? true : producerFulfillmentMethods.has(fulfillmentFilter);

    // A maker with no listings at all still answers "any category" — the filter
    // narrows to makers who sell the thing, it doesn't hide makers we know least
    // about only when a category is picked.
    const matchesCategory =
      !selectedCategory || (producerCategoriesById.get(producer.id)?.has(selectedCategory) ?? false);
    const matchesSoldOut = showSoldOut || (producerHasStockById.get(producer.id) ?? false);

    // A producer we can't place can't be excluded by a radius — same call the
    // items list makes, so an unknown distance never silently hides someone.
    const rawProducerDistance =
      withinMiles == null
        ? null
        : getDistanceMiles({
            buyerLocation,
            producerLocation: getResolvedProducerLocation(producer),
            fallbackBuyerZip: buyerDistanceFallbackZip,
            fallbackProducerZip: producer.zipCode ?? null,
          });
    // Guard non-finite as well as null: bad coordinates can produce NaN, which
    // would fail every comparison and silently hide the maker.
    const producerDistance =
      typeof rawProducerDistance === "number" && Number.isFinite(rawProducerDistance)
        ? rawProducerDistance
        : null;
    const matchesDistance =
      withinMiles == null || producerDistance == null || producerDistance <= withinMiles;

    return (
      matchesSearch &&
      matchesType &&
      matchesFulfillment &&
      matchesCategory &&
      matchesSoldOut &&
      matchesDistance
    );
  });

  const sortedProducers = [...filteredProducers].sort((a, b) => {
    if (producerSortOption === "name_az") {
      const aName =
        a.displayName || [a.firstName, a.lastName].filter(Boolean).join(" ") || "Local Producer";
      const bName =
        b.displayName || [b.firstName, b.lastName].filter(Boolean).join(" ") || "Local Producer";
      return aName.localeCompare(bName);
    }

    if (producerSortOption === "nearest") {
      const fallbackBuyerZip =
        buyerLocation?.zipCode?.trim() || zipCode?.trim() || user?.zipCode || null;

      const aDistance = getDistanceMiles({
        buyerLocation,
        producerLocation: getResolvedProducerLocation(a),
        fallbackBuyerZip,
        fallbackProducerZip: a.zipCode ?? null,
      });

      const bDistance = getDistanceMiles({
        buyerLocation,
        producerLocation: getResolvedProducerLocation(b),
        fallbackBuyerZip,
        fallbackProducerZip: b.zipCode ?? null,
      });

      if (aDistance == null && bDistance == null) return 0;
      if (aDistance == null) return 1;
      if (bDistance == null) return -1;

      return aDistance - bDistance;
    }

    const aFeatured = a.featuredUntil ? new Date(a.featuredUntil).getTime() > Date.now() : false;
    const bFeatured = b.featuredUntil ? new Date(b.featuredUntil).getTime() > Date.now() : false;
    if (aFeatured && !bFeatured) return -1;
    if (!aFeatured && bFeatured) return 1;
    return 0;
  });

  // The maker profile page swipes through producers in exactly this order.
  // Publishing the rendered list is what keeps that swipe on the browse
  // contract -- the profile page must never re-implement these filters.
  const producerOrderSignature = sortedProducers.map((producer) => producer.id).join(",");
  useEffect(() => {
    if (browseMode !== "producers" || isLoadingProducers || sortedProducers.length === 0) return;
    try {
      window.sessionStorage.setItem(
        "stoop.producerSwipeOrder",
        JSON.stringify(
          sortedProducers.map((producer) => ({
            id: producer.id,
            name:
              stripDemoMarker(
                producer.displayName ||
                  [producer.firstName, producer.lastName].filter(Boolean).join(" "),
              ) || "Local Producer",
          })),
        ),
      );
    } catch { /* private mode */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browseMode, isLoadingProducers, producerOrderSignature]);

  const { data: presentCategories = [] } = useQuery<string[]>({
    queryKey: ["/api/item-categories"],
    queryFn: async () => {
      const res = await fetch("/api/item-categories", { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
  });
  const categories = BROWSE_CATEGORIES.filter((c) => presentCategories.includes(c));

  const handleAddToCart = (
    itemId: string,
    qty: number,
    maxQty?: number,
    weightUnit?: { id: string; weight: number; priceCents: number },
  ) => {
    if (weightUnit) {
      addToCart(itemId, 1, undefined, undefined, undefined, weightUnit);
      toast({ title: "Added to cart", description: unitDisplayLabel({ label: (weightUnit as any).label, weightCentiLb: weightUnit.weight }), duration: 5000 });
      return;
    }
    const existing = cart.find((entry) => entry.itemId === itemId)?.qty ?? 0;
    if (typeof maxQty === "number" && existing >= maxQty) {
      toast({
        title: "At stock limit",
        description: `Only ${maxQty} available from this seller.`,
      });
      return;
    }
    const added =
      typeof maxQty === "number" ? Math.max(0, Math.min(qty, maxQty - existing)) : qty;
    if (added <= 0) return;
    addToCart(itemId, added, maxQty);
    toast({
      title: "Added to cart",
      description: `${added} item${added > 1 ? "s" : ""} added to your cart`,
    });
  };

  const handleCheckout = () => {
    setIsCartOpen(false);
    setLocation("/checkout");
  };

  const handleUseCurrentLocation = async () => {
    if (typeof window === "undefined") return;

    setIsLocatingBuyer(true);
    setBuyerLocationError(null);

    const onSuccess = (latitude: number, longitude: number) => {
      setBuyerLocation({
        source: "browser_gps",
        label: null,
        zipCode: zipCode?.trim() || user?.zipCode || null,
        point: { latitude, longitude },
      });
      setIsLocatingBuyer(false);
      reverseGeocodeCity(latitude, longitude).then((city) => {
        if (!city) return;
        setBuyerLocation((prev) =>
          prev && prev.source === "browser_gps" ? { ...prev, label: city } : prev,
        );
      });
    };

    const onError = (message: string) => {
      setBuyerLocationError(message);
      setIsLocatingBuyer(false);
    };

    if (Capacitor.isNativePlatform()) {
      try {
        const { Geolocation } = await import("@capacitor/geolocation");
        const perm = await Geolocation.requestPermissions();
        if (perm.location === "denied") {
          onError("Location permission was denied. Check your device settings.");
          return;
        }
        const pos = await Geolocation.getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 10000,
        });
        onSuccess(pos.coords.latitude, pos.coords.longitude);
      } catch (e: any) {
        onError(e?.message || "Could not get current location.");
      }
      return;
    }

    if (!("geolocation" in navigator)) {
      onError("Location is not supported on this device/browser.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => onSuccess(position.coords.latitude, position.coords.longitude),
      (error) => {
        const message =
          error.code === error.PERMISSION_DENIED
            ? "Location permission was denied."
            : error.code === error.POSITION_UNAVAILABLE
              ? "Current location is unavailable."
              : error.code === error.TIMEOUT
                ? "Location request timed out."
                : "Could not get current location.";
        onError(message);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
    );
  };

  const showBoxesResults = browseMode === "boxes";
  const showProductResults = browseMode === "products";
  const showProducersResults = browseMode === "producers";
  // Saved and Following used to ignore the search box entirely: typing a query
  // filtered Listings/Boxes/Growers but left these two showing everything, so
  // search looked broken depending on which tab you were on. Same matcher as
  // the typeahead, so a query means the same thing on every surface.
  const searchListItems = <T extends Item & { seller?: User }>(rows: T[]): T[] => {
    const q = searchQuery.trim();
    if (!q) return rows;
    return rows.filter((row) => matchesQuery(row));
  };
  const visibleSavedItems = searchListItems(savedFeedItems);
  const visibleFollowingItems = searchListItems(followingFeedItems);

  const showFollowingResults = browseMode === "following";
  const showSavedResults = browseMode === "saved";
  const showStickyFilterBar = !showFollowingResults && !showSavedResults;

  const fulfillmentFilterLoading =
    showProductResults &&
    fulfillmentFilter !== "any" &&
    items.length > 0 &&
    fulfillmentQueries.some((query) => query.isLoading);
  const activeCount = showSavedResults
    ? visibleSavedItems.length
    : showFollowingResults
      ? visibleFollowingItems.length
      : showBoxesResults
        ? filteredBoxes.length
        : showProductResults
          ? sortedItems.length
          : sortedProducers.length;
  const activeLoading = showSavedResults
    ? isLoadingSaved
    : showFollowingResults
      ? isLoadingFollowing
      : showBoxesResults
        ? isLoadingBoxes
        : showProductResults
          ? isLoading || fulfillmentFilterLoading
          : isLoadingProducers;
  // Queries default to retry:false, so a request that dies with the connection
  // lands here immediately. Without this the list falls through to "No items
  // found", which reads as "your neighborhood is empty" rather than "you're
  // offline" — the one state we must not get wrong when someone loses signal.
  const activeError = showSavedResults
    ? savedError
    : showFollowingResults
      ? followingError
      : showBoxesResults
        ? boxesError
        : showProductResults
          ? itemsError
          : producersError;
  const refetchActive = showSavedResults
    ? refetchSaved
    : showFollowingResults
      ? refetchFollowing
      : showBoxesResults
        ? refetchBoxes
        : showProductResults
          ? refetchItems
          : refetchProducers;

  return (
    <div className={embedded ? "" : "min-h-screen flex flex-col bg-background"}>
      {!embedded && (
        <Header
          cartCount={cartCount}
          onCartClick={() => setIsCartOpen(true)}
          collapsed={false}
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          locationLabel={
            buyerLocation?.source === "browser_gps"
              ? buyerLocation.label || "Near you"
              : zipCode?.trim() || user?.zipCode
                ? `ZIP ${zipCode?.trim() || user?.zipCode}`
                : "Set location"
          }
          onUseCurrentLocation={handleUseCurrentLocation}
          isLocating={isLocatingBuyer}
          locationError={buyerLocationError}
        />
      )}

      <main className={embedded ? "" : "flex-1"}>
        {!embedded && (
          <>
            <CountdownBanner />
            <CommunityChampions />
            <ListFirstProductBanner />
            <InviteNudgeBanner />
            <div className="bg-card border-b">
              <div className="container mx-auto px-4 py-3">
                <div className="grid grid-cols-1 gap-2">
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={
                        showBoxesResults
                          ? "Search weekly boxes"
                          : showProductResults
                            ? "Search local products"
                            : "Search growers and makers"
                      }
                      className={searchQuery.trim() ? "pl-9 pr-9" : "pl-9"}
                      data-testid="input-search"
                    />
                    {searchQuery.trim() !== "" && (
                      <button
                        type="button"
                        aria-label="Clear search"
                        onClick={() => {
                          setSearchQuery("");
                          broadcastFilters({ q: "" });
                        }}
                        className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground hover:bg-muted"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Embedded, the pills live in the wrapper and Saved/Following have no
            filters, so this bar would render as an empty padded strip under the
            pill bar. */}
        {(!embedded || showStickyFilterBar) && (
        <div className="sticky z-40" style={{ top: embedded ? props.stickyTop ?? 0 : "calc(54px + var(--safe-area-top, 0px))" }}>
          <div className="border-b bg-card">
            <div className="container mx-auto px-4 py-1">
              {!embedded && (
                <div className="-mx-2 flex justify-center gap-0.5 pb-0.5">
                  <button type="button" onClick={() => goMode("boxes")} className={`shrink-0 rounded-full px-1.5 py-0.5 text-[11px] font-medium whitespace-nowrap ${browseMode === "boxes" ? "bg-primary text-primary-foreground" : "border border-border text-foreground"}`}>
                    Boxes
                  </button>
                  <button type="button" onClick={() => { goMode("products"); setSelectedCategory(null); }} className={`shrink-0 rounded-full px-1.5 py-0.5 text-[11px] font-medium whitespace-nowrap ${browseMode === "products" ? "bg-primary text-primary-foreground" : "border border-border text-foreground"}`}>
                    Listings
                  </button>
                  <button type="button" onClick={() => goMode("producers")} className={`shrink-0 rounded-full px-1.5 py-0.5 text-[11px] font-medium whitespace-nowrap ${browseMode === "producers" ? "bg-primary text-primary-foreground" : "border border-border text-foreground"}`}>
                    Growers & Makers
                  </button>
                  <button type="button" onClick={() => goMode("saved")} className={`shrink-0 rounded-full px-1.5 py-0.5 text-[11px] font-medium whitespace-nowrap ${browseMode === "saved" ? "bg-primary text-primary-foreground" : "border border-border text-foreground"}`}>
                    Saved
                  </button>
                  <button type="button" onClick={() => goMode("following")} className={`shrink-0 rounded-full px-1.5 py-0.5 text-[11px] font-medium whitespace-nowrap ${browseMode === "following" ? "bg-primary text-primary-foreground" : "border border-border text-foreground"}`}>
                    Following
                  </button>
                </div>
              )}
              {/* Full-bleed on purpose. The pager only gives up a drag that
                  STARTS on a horizontally scrolling element, so any dead padding
                  around this row is a strip where trying to scroll the filters
                  swipes to the next tab instead. The negative margins pull the
                  scroller out into the container's padding and the matching
                  padding puts the chips back where they were, so the touch
                  target is the row's whole width and height without moving
                  anything. */}
              {!showFollowingResults && !showSavedResults && (
                <div
                  className="-mx-4 -my-1 flex gap-1 overflow-x-auto px-4 py-1.5 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden [touch-action:pan-x_pan-y]"
                  // When the row overflows, its drags are its own: don't let the
                  // pager's pointer handlers see them at all. The WebView froze
                  // this row once the search chip led it — Embla declined the
                  // drag (correctly) but the row never got the pan either, so
                  // dragging did nothing. Capture-phase and conditional, so a
                  // row that fits still hands swipes to the pager as before.
                  onPointerDownCapture={(e) => {
                    const el = e.currentTarget;
                    if (el.scrollWidth > el.clientWidth + 1) e.stopPropagation();
                  }}
                >
                  {/* Leads the row only while something is filtered, so it reads
                      as a reminder of what's narrowing the list and clears the
                      lot in one tap. The filters it clears are shared, so this
                      resets Listings, Growers & Makers and the map together. */}
                  {activeSharedFilterCount > 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={clearSharedFilters}
                      className="h-6 shrink-0 rounded-lg pl-2.5 pr-2 text-[9px] leading-none whitespace-nowrap border-primary bg-primary text-primary-foreground hover:bg-primary/90"
                      data-testid="button-clear-shared-filters"
                    >
                      <span>{activeSharedFilterCount} filter{activeSharedFilterCount === 1 ? "" : "s"}</span>
                      <X className="ml-1 h-2.5 w-2.5" />
                    </Button>
                  )}
                  {/* The search narrows this list exactly like a filter, so it
                      gets a chip that says so and clears in one tap. Without it
                      a query typed on another tab silently hid results here with
                      nothing on screen to undo it. */}
                  {searchQuery.trim() !== "" && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSearchQuery("");
                        broadcastFilters({ q: "" });
                      }}
                      className="h-6 shrink-0 rounded-lg pl-2.5 pr-2 text-[9px] leading-none whitespace-nowrap border-primary bg-primary text-primary-foreground hover:bg-primary/90"
                      data-testid="button-clear-search"
                    >
                      <Search className="mr-1 h-2.5 w-2.5" />
                      <span className="max-w-[9rem] truncate">{searchQuery.trim()}</span>
                      <X className="ml-1 h-2.5 w-2.5" />
                    </Button>
                  )}
                  {(showBoxesResults || showProductResults) ? (
                    <>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm" className={`h-6 rounded-lg pl-2.5 pr-2 text-[9px] leading-none whitespace-nowrap ${fulfillmentFilter === "any" ? "border-primary/25 bg-primary/5 text-primary hover:bg-primary/10" : "border-primary/35 bg-primary/12 text-primary hover:bg-primary/16"}`} data-testid="button-fulfillment-filter-menu">
                            <ListFilter className="mr-0 h-2 w-2" />
                            <span>{fulfillmentFilter === "any" ? "Fulfillment" : fulfillmentFilter === "seller_pickup" ? "Pickup" : fulfillmentFilter === "drop_zone_pickup" ? "Hub" : "Delivery"}</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                          <DropdownMenuLabel>Fulfillment</DropdownMenuLabel>
                          <DropdownMenuRadioGroup value={fulfillmentFilter} onValueChange={(v) => setSharedFulfillment(v as FulfillmentFilter)}>
                            <DropdownMenuRadioItem value="any">Any fulfillment</DropdownMenuRadioItem>
                            <DropdownMenuRadioItem value="seller_pickup" onClick={() => { setSharedFulfillment("seller_pickup"); window.dispatchEvent(new CustomEvent("mission:filter-fulfillment")); }}>Pickup from producer</DropdownMenuRadioItem>
                            <DropdownMenuRadioItem value="drop_zone_pickup" onClick={() => { setSharedFulfillment("drop_zone_pickup"); window.dispatchEvent(new CustomEvent("mission:filter-fulfillment")); }}>Community hub</DropdownMenuRadioItem>
                            <DropdownMenuRadioItem value="local_delivery" onClick={() => { setSharedFulfillment("local_delivery"); window.dispatchEvent(new CustomEvent("mission:filter-fulfillment")); }}>Local delivery</DropdownMenuRadioItem>
                          </DropdownMenuRadioGroup>
                        </DropdownMenuContent>
                      </DropdownMenu>

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm" className={`h-6 rounded-lg pl-2.5 pr-2 text-[9px] leading-none whitespace-nowrap ${sortOption === "default" ? "border-primary/25 bg-primary/5 text-primary hover:bg-primary/10" : "border-primary/35 bg-primary/12 text-primary hover:bg-primary/16"}`} data-testid="button-sort-menu">
                            <ListFilter className="mr-0 h-2 w-2" />
                            <span>{sortOption === "default" ? "Sort" : sortOption === "nearest" ? "Nearest" : sortOption === "price_low" ? "Price Up" : "Price Down"}</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                          <DropdownMenuLabel>Sort</DropdownMenuLabel>
                          <DropdownMenuRadioGroup value={sortOption} onValueChange={(v) => setSortOption(v as SortOption)}>
                            <DropdownMenuRadioItem value="default">Default</DropdownMenuRadioItem>
                            <DropdownMenuRadioItem value="nearest" onClick={() => { setSortOption("nearest"); window.dispatchEvent(new CustomEvent("mission:sort-distance")); }}>Nearest</DropdownMenuRadioItem>
                            <DropdownMenuRadioItem value="price_low">Price: Low to High</DropdownMenuRadioItem>
                            <DropdownMenuRadioItem value="price_high">Price: High to Low</DropdownMenuRadioItem>
                          </DropdownMenuRadioGroup>
                        </DropdownMenuContent>
                      </DropdownMenu>

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm" className={`h-6 rounded-lg pl-2.5 pr-2 text-[9px] leading-none whitespace-nowrap ${withinMiles == null ? "border-primary/25 bg-primary/5 text-primary hover:bg-primary/10" : "border-primary/35 bg-primary/12 text-primary hover:bg-primary/16"}`} data-testid="button-distance-menu">
                            <MapPin className="mr-0 h-2 w-2" />
                            <span>{withinMiles == null ? "Distance" : `${withinMiles} mi`}</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                          <DropdownMenuLabel>Within</DropdownMenuLabel>
                          <DropdownMenuRadioGroup
                            value={withinMiles == null ? "any" : String(withinMiles)}
                            onValueChange={(v) => setSharedWithinMiles(v === "any" ? null : Number(v))}
                          >
                            <DropdownMenuRadioItem value="any">Any distance</DropdownMenuRadioItem>
                            {DISTANCE_OPTIONS.map((mi) => (
                              <DropdownMenuRadioItem key={mi} value={String(mi)}>
                                Within {mi} miles
                              </DropdownMenuRadioItem>
                            ))}
                          </DropdownMenuRadioGroup>
                        </DropdownMenuContent>
                      </DropdownMenu>

                      <Button type="button" variant="outline" size="sm" onClick={() => setSharedShowSoldOut(!showSoldOut)} className={`h-6 rounded-lg pl-2.5 pr-2 text-[9px] leading-none whitespace-nowrap ${showSoldOut ? "border-primary/35 bg-primary/12 text-primary hover:bg-primary/16" : "border-primary/25 bg-primary/5 text-primary hover:bg-primary/10"}`} data-testid="button-toggle-soldout">
                        <span className={`mr-1 inline-flex h-3.5 w-6 items-center rounded-full border transition ${showSoldOut ? "border-primary/40 bg-primary/20 justify-end" : "border-primary/20 bg-background justify-start"}`}>
                          <span className={`mx-[1px] h-2.5 w-2.5 rounded-full transition ${showSoldOut ? "bg-primary" : "bg-muted-foreground/60"}`} />
                        </span>
                        <span>Show Sold Out</span>
                      </Button>

                      {/* Only rendered once at least one item actually qualifies —
                          invisible to everyone until a seller (or the whole
                          platform) is SNAP-approved. */}
                      {anyEbtEligibleItems && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setEbtOnly((c) => !c)}
                          className={`h-6 rounded-lg pl-2.5 pr-2 text-[9px] leading-none whitespace-nowrap ${ebtOnly ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/20 dark:text-emerald-400" : "border-emerald-500/30 bg-emerald-500/5 text-emerald-700 hover:bg-emerald-500/10 dark:text-emerald-400"}`}
                          data-testid="button-toggle-ebt"
                        >
                          <Landmark className="mr-1 h-2.5 w-2.5" />
                          <span>EBT / SNAP</span>
                        </Button>
                      )}
                    </>
                  ) : (
                    <>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm" className={`h-6 rounded-lg pl-2.5 pr-2 text-[9px] leading-none whitespace-nowrap ${fulfillmentFilter === "any" ? "border-primary/25 bg-primary/5 text-primary hover:bg-primary/10" : "border-primary/35 bg-primary/12 text-primary hover:bg-primary/16"}`} data-testid="button-producer-fulfillment-menu">
                            <ListFilter className="mr-0 h-2 w-2" />
                            <span>{fulfillmentFilter === "any" ? "Fulfillment" : fulfillmentFilter === "seller_pickup" ? "Pickup" : fulfillmentFilter === "drop_zone_pickup" ? "Hub" : "Delivery"}</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                          <DropdownMenuLabel>Fulfillment</DropdownMenuLabel>
                          <DropdownMenuRadioGroup value={fulfillmentFilter} onValueChange={(v) => setSharedFulfillment(v as FulfillmentFilter)}>
                            <DropdownMenuRadioItem value="any">Any fulfillment</DropdownMenuRadioItem>
                            <DropdownMenuRadioItem value="seller_pickup" onClick={() => { setSharedFulfillment("seller_pickup"); window.dispatchEvent(new CustomEvent("mission:filter-fulfillment")); }}>Pickup from producer</DropdownMenuRadioItem>
                            <DropdownMenuRadioItem value="drop_zone_pickup" onClick={() => { setSharedFulfillment("drop_zone_pickup"); window.dispatchEvent(new CustomEvent("mission:filter-fulfillment")); }}>Community hub</DropdownMenuRadioItem>
                            <DropdownMenuRadioItem value="local_delivery" onClick={() => { setSharedFulfillment("local_delivery"); window.dispatchEvent(new CustomEvent("mission:filter-fulfillment")); }}>Local delivery</DropdownMenuRadioItem>
                          </DropdownMenuRadioGroup>
                        </DropdownMenuContent>
                      </DropdownMenu>

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm" className={`h-6 rounded-lg pl-2.5 pr-2 text-[9px] leading-none whitespace-nowrap ${producerSortOption === "default" ? "border-primary/25 bg-primary/5 text-primary hover:bg-primary/10" : "border-primary/35 bg-primary/12 text-primary hover:bg-primary/16"}`} data-testid="button-producer-sort-menu">
                            <ListFilter className="mr-0 h-2 w-2" />
                            <span>{producerSortOption === "default" ? "Sort" : producerSortOption === "nearest" ? "Nearest" : "A–Z"}</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                          <DropdownMenuLabel>Sort</DropdownMenuLabel>
                          <DropdownMenuRadioGroup value={producerSortOption} onValueChange={(v) => setProducerSortOption(v as ProducerSortOption)}>
                            <DropdownMenuRadioItem value="default">Default</DropdownMenuRadioItem>
                            <DropdownMenuRadioItem value="nearest" onClick={() => { setProducerSortOption("nearest"); window.dispatchEvent(new CustomEvent("mission:sort-distance")); }}>Nearest</DropdownMenuRadioItem>
                            <DropdownMenuRadioItem value="name_az">Name: A to Z</DropdownMenuRadioItem>
                          </DropdownMenuRadioGroup>
                        </DropdownMenuContent>
                      </DropdownMenu>

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm" className={`h-6 rounded-lg pl-2.5 pr-2 text-[9px] leading-none whitespace-nowrap ${producerTypeFilter === "any" ? "border-primary/25 bg-primary/5 text-primary hover:bg-primary/10" : "border-primary/35 bg-primary/12 text-primary hover:bg-primary/16"}`} data-testid="button-producer-type-menu">
                            <ListFilter className="mr-0 h-2 w-2" />
                            <span>{producerTypeFilter === "any" ? "Type" : producerTypeFilter === "farmstand" ? "Farm Stand" : prettyLabel(producerTypeFilter)}</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                          <DropdownMenuLabel>Type</DropdownMenuLabel>
                          <DropdownMenuRadioGroup value={producerTypeFilter} onValueChange={(v) => setProducerTypeFilter(v as ProducerTypeFilter)}>
                            <DropdownMenuRadioItem value="any">Any type</DropdownMenuRadioItem>
                            <DropdownMenuRadioItem value="grower">Grower</DropdownMenuRadioItem>
                            <DropdownMenuRadioItem value="maker">Maker</DropdownMenuRadioItem>
                            <DropdownMenuRadioItem value="farmstand">Farm Stand</DropdownMenuRadioItem>
                            <DropdownMenuRadioItem value="mixed">Mixed</DropdownMenuRadioItem>
                          </DropdownMenuRadioGroup>
                        </DropdownMenuContent>
                      </DropdownMenu>

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm" className={`h-6 rounded-lg pl-2.5 pr-2 text-[9px] leading-none whitespace-nowrap ${withinMiles == null ? "border-primary/25 bg-primary/5 text-primary hover:bg-primary/10" : "border-primary/35 bg-primary/12 text-primary hover:bg-primary/16"}`} data-testid="button-distance-menu">
                            <MapPin className="mr-0 h-2 w-2" />
                            <span>{withinMiles == null ? "Distance" : `${withinMiles} mi`}</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                          <DropdownMenuLabel>Within</DropdownMenuLabel>
                          <DropdownMenuRadioGroup
                            value={withinMiles == null ? "any" : String(withinMiles)}
                            onValueChange={(v) => setSharedWithinMiles(v === "any" ? null : Number(v))}
                          >
                            <DropdownMenuRadioItem value="any">Any distance</DropdownMenuRadioItem>
                            {DISTANCE_OPTIONS.map((mi) => (
                              <DropdownMenuRadioItem key={mi} value={String(mi)}>
                                Within {mi} miles
                              </DropdownMenuRadioItem>
                            ))}
                          </DropdownMenuRadioGroup>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </>
                  )}
                </div>
              )}
            </div>
            {/* Growers & Makers gets the same strip as Listings: the category is
                one shared value, so arriving from Listings already shows it
                selected, and picking one here narrows makers to those who sell
                it. */}
            {(showProductResults || showProducersResults) && (
              <CategoryChips
                categories={categories.filter((c): c is string => typeof c === "string")}
                selectedCategory={selectedCategory}
                onSelectCategory={setSharedCategory}
              />
            )}
          </div>
        </div>
        )}

        <div className="container mx-auto px-4 py-0 sm:py-6">
          <div className="mb-4 flex items-center justify-between gap-3 sm:mb-6">
            <div className="hidden sm:block">
              <h1 className="text-3xl md:text-4xl font-semibold mb-2" data-testid="text-browse-title">
                {showBoxesResults
                  ? `Weekly Boxes in ${zipCode || "All ZIPs"}`
                  : showProductResults
                    ? `Local Listings near ${zipCode || "you"}`
                    : `Growers & Makers in ${zipCode || "All ZIPs"}`}
              </h1>
              <p className="text-muted-foreground">
                {activeCount} {showBoxesResults ? `box${activeCount !== 1 ? "es" : ""}` : showProductResults ? `product${activeCount !== 1 ? "s" : ""}` : `profile${activeCount !== 1 ? "s" : ""}`} available
              </p>
            </div>
          </div>

          {/* Search reaches across tabs: a query typed for products almost always
              has box and maker matches too, and boxes are a headline surface, not
              a fallback. Surface both as peer jumps at the top of the results so a
              search never strands them — including when products itself is empty. */}
          {showProductResults && searchQuery.trim() !== "" && (filteredBoxes.length > 0 || filteredProducers.length > 0) && (
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <span className="text-xs text-muted-foreground">Also matching “{searchQuery.trim()}”:</span>
              {filteredBoxes.length > 0 && (
                <button
                  type="button"
                  onClick={() => goMode("boxes")}
                  className="rounded-full border border-border bg-background px-3 py-1 text-xs font-medium hover:bg-muted"
                  data-testid="pivot-boxes"
                >
                  Boxes ({filteredBoxes.length})
                </button>
              )}
              {filteredProducers.length > 0 && (
                <button
                  type="button"
                  onClick={() => goMode("producers")}
                  className="rounded-full border border-border bg-background px-3 py-1 text-xs font-medium hover:bg-muted"
                  data-testid="pivot-producers"
                >
                  Growers &amp; Makers ({filteredProducers.length})
                </button>
              )}
            </div>
          )}

          {!activeLoading && showBoxesResults && (() => {
            const cookZip =
              buyerLocation?.zipCode?.trim() ||
              zipCode?.trim() ||
              user?.zipCode?.trim() ||
              "";
            if (!cookZip) return null;
            return (
            <Link
              href={`/recipes?zip=${encodeURIComponent(cookZip)}`}
              className="mb-5 block sm:mb-6"
              data-testid="link-cook-this-week"
            >
              <Card className="overflow-hidden border-primary/30 bg-gradient-to-br from-primary/10 via-card to-amber-50 transition hover:shadow-md">
                <CardContent className="flex items-center gap-3 p-4 sm:gap-4 sm:p-5">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/15 text-primary sm:h-14 sm:w-14">
                    <ChefHat className="h-6 w-6 sm:h-7 sm:w-7" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <h3 className="text-[15px] font-semibold leading-tight sm:text-base">Cook this week</h3>
                      <Sparkles className="h-3.5 w-3.5 text-amber-500" />
                    </div>
                    <p className="mt-0.5 text-xs text-muted-foreground sm:text-sm">
                      Pick recipes made from what your neighbors have. We&rsquo;ll build the box for you.
                    </p>
                  </div>
                  <span className="shrink-0 rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground">
                    Plan
                  </span>
                </CardContent>
              </Card>
            </Link>
            );
          })()}

          {!activeLoading && showBoxesResults && (
            <div className="mb-5 sm:mb-6">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold">This Week’s Boxes</h2>
                  <p className="text-sm text-muted-foreground">
                    Single-seller boxes first, plus nearby-pair boxes when sellers are close enough.
                  </p>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setBoxesDeliveryOnly((current) => !current)}
                  className={`rounded-full whitespace-nowrap ${
                    boxesDeliveryOnly
                      ? "border-primary/35 bg-primary/12 text-primary hover:bg-primary/16"
                      : "border-primary/25 bg-primary/5 text-primary hover:bg-primary/10"
                  }`}
                  data-testid="button-boxes-delivery-filter"
                >
                  <Truck className="mr-1 h-4 w-4" />
                  {boxesDeliveryOnly ? "Delivery Available" : "All Boxes"}
                </Button>
              </div>

              {isLoadingBoxes ? (
                <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading boxes...
                </div>
              ) : sortedBoxes.length > 0 ? (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  {sortedBoxes.map((box) => {
                    const boxBuyerDistanceLabel = (() => {
                      const distances = box.sellerIds
                        .map((sellerId) => {
                          const producer = producerById.get(sellerId);
                          if (!producer) return null;

                          const miles = getDistanceMiles({
                            buyerLocation,
                            producerLocation: getResolvedProducerLocation(producer),
                            fallbackBuyerZip: buyerDistanceFallbackZip,
                            fallbackProducerZip: producer.zipCode ?? null,
                          });

                          return typeof miles === "number" && Number.isFinite(miles) ? miles : null;
                        })
                        .filter((value): value is number => value != null);

                      if (distances.length === 0) return null;

                      const nearest = Math.min(...distances);
                      return nearest <= 2 ? "Near you" : `~${nearest} mi`;
                    })();

                    return (
                    <Card
                      key={box.id}
                      className="cursor-pointer overflow-hidden border-primary/15 transition hover:-translate-y-0.5 hover:shadow-md"
                      data-testid={`card-box-${box.id}`}
                      onClick={() => setSelectedBox(box)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setSelectedBox(box);
                        }
                      }}
                    >
                      <CardContent className="p-2.5 sm:p-3">
                        <div className="mb-2 flex items-start justify-between gap-2">
                          <div className="min-w-0 flex items-center overflow-hidden rounded-2xl">
                            {box.items.slice(0, 4).map((item, index) => {
                              const sourceItem = items.find((entry) => entry.id === item.id);
                              const photo = getListingImages(sourceItem)[0];

                              return (
                                <div
                                  key={`box-image-${item.id}`}
                                  className="relative h-14 w-14 shrink-0 overflow-hidden rounded-2xl border-2 border-background bg-muted shadow-sm sm:h-16 sm:w-16"
                                  style={{ marginLeft: index === 0 ? 0 : "-10px", zIndex: 10 - index }}
                                >
                                  {photo ? (
                                    <img
                                      src={photo}
                                      alt={stripDemoMarker(item.name) || item.name}
                                      loading="lazy"
                                      decoding="async"
                                      className="h-full w-full object-cover"
                                    />
                                  ) : (
                                    <ListingPhotoPlaceholder category={sourceItem?.category} size="xs" />
                                  )}
                                </div>
                              );
                            })}

                            {box.items.length > 4 ? (
                              <div className="ml-1 inline-flex h-7 w-7 items-center justify-center rounded-full border bg-card text-[10px] font-medium text-muted-foreground">
                                +{box.items.length - 4}
                              </div>
                            ) : null}
                          </div>

                          <div className="flex shrink-0 flex-col items-end gap-1">
                            <button
                              type="button"
                              className="flex h-7 w-7 items-center justify-center rounded-full border border-border bg-background/90 transition-transform active:scale-90"
                              onClick={(e) => {
                                e.stopPropagation();
                                const allSaved = box.items.every((i) => savedSet.has(i.id));
                                if (allSaved) {
                                  for (const item of box.items) toggleSave(item.id);
                                } else {
                                  for (const item of box.items) {
                                    if (!savedSet.has(item.id)) toggleSave(item.id);
                                  }
                                }
                              }}
                              aria-label={box.items.every((i) => savedSet.has(i.id)) ? "Unsave box" : "Save box"}
                            >
                              <Heart
                                className={[
                                  "h-3.5 w-3.5",
                                  box.items.every((i) => savedSet.has(i.id))
                                    ? "fill-red-500 text-red-500"
                                    : "text-muted-foreground",
                                ].join(" ")}
                              />
                            </button>
                            <span className="inline-flex items-center rounded-full border border-primary/20 bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-foreground">
                              {box.boxType === "single_seller" ? "Single Seller" : "Nearby Pair"}
                            </span>

                            {box.deliveryAvailable && (
                              <span className="inline-flex items-center gap-1 rounded-full border border-amber-200/80 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-800">
                                <Truck className="h-3 w-3 text-amber-600" />
                                Delivery
                              </span>
                            )}

                            {box.pickupAvailable && (
                              <span className="inline-flex items-center gap-1 rounded-full border border-primary/20 bg-primary/15 px-2 py-0.5 text-[10px] font-medium text-foreground">
                                <ShoppingBasket className="h-3 w-3 text-primary" />
                                Pickup
                              </span>
                            )}
                          </div>
                        </div>

                        <h3 className="text-[15px] font-semibold leading-tight sm:text-base">{box.title}</h3>

                        <p className="mt-0.5 line-clamp-1 text-xs sm:text-sm text-muted-foreground">
                          {box.sellerNames.join(" + ")}
                        </p>

                        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] sm:text-xs text-muted-foreground">
                          <span>{box.itemCount} items</span>
                          <span>${box.subtotal.toFixed(2)}</span>
                          {box.boxType === "nearby_pair" && box.distanceMiles != null ? (
                            <span>Sellers {box.distanceMiles.toFixed(1)} mi apart</span>
                          ) : null}
                        </div>

                        <div className="mt-2 space-y-1.5">
                          {(box.items.length <= 4 ? box.items : box.items.slice(0, 4)).map((item) => (
                            <div key={item.id} className="flex items-start justify-between gap-3 text-[13px] sm:text-sm">
                              <span className="min-w-0 leading-snug">{stripDemoMarker(item.name) || item.name}</span>
                              <span className="whitespace-nowrap text-muted-foreground">
                                ${Number(item.price).toFixed(2)}
                              </span>
                            </div>
                          ))}

                          {box.items.length > 4 ? (
                            <button
                              type="button"
                              className="text-[12px] font-medium text-primary"
                              onClick={(event) => {
                                event.stopPropagation();
                                setSelectedBox(box);
                              }}
                              data-testid={`button-open-box-from-more-${box.id}`}
                            >
                              +{box.items.length - 4} more
                            </button>
                          ) : null}
                        </div>

                        <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] sm:text-xs text-muted-foreground">
                          {boxBuyerDistanceLabel ? <span>To you {boxBuyerDistanceLabel}</span> : null}
                          {box.deliveryAvailable ? (
                            <span>
                              {box.deliveryMinimum != null ? `Min $${box.deliveryMinimum.toFixed(2)}` : "Delivery"}
                              {box.deliveryFee != null ? ` • Fee $${box.deliveryFee.toFixed(2)}` : ""}
                            </span>
                          ) : (
                            <span>Pickup only</span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                    );
                  })}
                </div>
              ) : (
                <div className="rounded-xl border bg-card p-4 text-sm text-muted-foreground">
                  No recommended boxes available yet for this view.
                </div>
              )}
            </div>
          )}

          {activeLoading && (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}

          {!activeLoading && showProductResults && sortedItems.length > 0 && (
            <div className="grid grid-cols-1 gap-3 sm:gap-4">
              {sortedItems.map((item) => (
                <ProductCard
                      key={item.id}
                      item={item}
                      onAddToCart={handleAddToCart}
                      buyerLocation={buyerLocation}
                      fallbackBuyerZip={buyerDistanceFallbackZip}
                      isSaved={savedSet.has(item.id)}
                      onToggleSave={toggleSave}
                      cartQty={cart.find((entry) => entry.itemId === item.id)?.qty ?? 0}
                    />
              ))}
            </div>
          )}

          {!activeLoading && showFollowingResults && (() => {
            const followingBoxes = recommendedBoxes.filter((box) =>
              box.sellerIds.some((sid) => followingSet.has(sid))
            );
            const hasContent = visibleFollowingItems.length > 0 || followingBoxes.length > 0;

            if (!hasContent) {
              return (
                <div className="flex flex-col items-center gap-4 py-16 text-center">
                  <Heart className="h-12 w-12 text-muted-foreground/40" />
                  <div>
                    <h3 className="text-lg font-semibold">
                      {searchQuery.trim() && followingFeedItems.length > 0
                        ? `Nothing you follow matches "${searchQuery.trim()}"`
                        : "No posts yet"}
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {searchQuery.trim() && followingFeedItems.length > 0
                        ? "Clear the search to see everything from sellers you follow."
                        : "Follow sellers you love to see their latest listings here."}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => goMode("producers")}
                    className="rounded-full"
                  >
                    Browse Growers & Makers
                  </Button>
                </div>
              );
            }

            return (
              <>
                {followingBoxes.length > 0 && (
                  <div className="mb-6">
                    <div className="mb-3">
                      <h2 className="text-xl font-bold tracking-tight sm:text-2xl">Boxes From Your Sellers</h2>
                      <p className="mt-1 text-sm text-muted-foreground">Curated boxes featuring sellers you follow</p>
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:gap-4">
                      {followingBoxes.map((box) => {
                        const boxBuyerDistanceLabel = (() => {
                          if (!box.distanceMiles) return null;
                          return `~${Math.round(box.distanceMiles)} mi`;
                        })();

                        return (
                          <Card
                            key={box.id}
                            className="cursor-pointer overflow-hidden border-primary/15 transition hover:-translate-y-0.5 hover:shadow-md"
                            onClick={() => setSelectedBox(box)}
                            role="button"
                            tabIndex={0}
                          >
                            <CardContent className="p-2.5 sm:p-3">
                              <div className="mb-2 flex items-start justify-between gap-2">
                                <div className="min-w-0 flex items-center overflow-hidden rounded-2xl">
                                  {box.items.slice(0, 4).map((item, index) => {
                                    const sourceItem = items.find((entry) => entry.id === item.id);
                                    const photo = getListingImages(sourceItem)[0];
                                    return (
                                      <div
                                        key={`follow-box-img-${item.id}`}
                                        className="relative h-14 w-14 shrink-0 overflow-hidden rounded-2xl border-2 border-background bg-muted shadow-sm sm:h-16 sm:w-16"
                                        style={{ marginLeft: index === 0 ? 0 : "-10px", zIndex: 10 - index }}
                                      >
                                        {photo ? (
                                          <img src={photo} alt={stripDemoMarker(item.name) || item.name} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                                        ) : (
                                          <ListingPhotoPlaceholder category={sourceItem?.category} size="xs" />
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                                <div className="flex shrink-0 flex-col items-end gap-1">
                                  <button
                                    type="button"
                                    className="flex h-7 w-7 items-center justify-center rounded-full border border-border bg-background/90 transition-transform active:scale-90"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const allSaved = box.items.every((i) => savedSet.has(i.id));
                                      if (allSaved) {
                                        for (const bi of box.items) toggleSave(bi.id);
                                      } else {
                                        for (const bi of box.items) {
                                          if (!savedSet.has(bi.id)) toggleSave(bi.id);
                                        }
                                      }
                                    }}
                                  >
                                    <Heart className={["h-3.5 w-3.5", box.items.every((i) => savedSet.has(i.id)) ? "fill-red-500 text-red-500" : "text-muted-foreground"].join(" ")} />
                                  </button>
                                  <span className="inline-flex items-center rounded-full border border-primary/20 bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-foreground">
                                    {box.boxType === "single_seller" ? "Single Seller" : "Nearby Pair"}
                                  </span>
                                </div>
                              </div>
                              <h3 className="text-[15px] font-semibold leading-tight sm:text-base">{box.title}</h3>
                              <p className="mt-0.5 line-clamp-1 text-xs sm:text-sm text-muted-foreground">{box.sellerNames.join(" + ")}</p>
                              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] sm:text-xs text-muted-foreground">
                                <span>{box.itemCount} items</span>
                                <span>${box.subtotal.toFixed(2)}</span>
                              </div>
                              <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] sm:text-xs text-muted-foreground">
                                {boxBuyerDistanceLabel ? <span>To you {boxBuyerDistanceLabel}</span> : null}
                                {box.deliveryAvailable ? <span>Delivery</span> : <span>Pickup only</span>}
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                )}

                {visibleFollowingItems.length > 0 && (
                  <div>
                    <div className="mb-3">
                      <h2 className="text-xl font-bold tracking-tight sm:text-2xl">Latest Listings</h2>
                      <p className="mt-1 text-sm text-muted-foreground">Recent items from sellers you follow</p>
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:gap-4">
                      {visibleFollowingItems.map((item) => (
                        <ProductCard
                          key={item.id}
                          item={item}
                          onAddToCart={handleAddToCart}
                          buyerLocation={buyerLocation}
                          fallbackBuyerZip={buyerDistanceFallbackZip}
                          isSaved={savedSet.has(item.id)}
                          onToggleSave={toggleSave}
                          cartQty={cart.find((entry) => entry.itemId === item.id)?.qty ?? 0}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            );
          })()}

          {!activeLoading && showSavedResults && (
            <>
              {visibleSavedItems.length === 0 ? (
                <div className="flex flex-col items-center gap-4 py-16 text-center">
                  <Heart className="h-12 w-12 text-muted-foreground/40" />
                  <div>
                    <h3 className="text-lg font-semibold">
                      {searchQuery.trim() && savedFeedItems.length > 0
                        ? `No saved items match "${searchQuery.trim()}"`
                        : "No saved items yet"}
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {searchQuery.trim() && savedFeedItems.length > 0
                        ? "Clear the search to see everything you've saved."
                        : "Tap the heart on any listing to save it for later."}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => goMode("products")}
                    className="rounded-full"
                  >
                    Browse Listings
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3 sm:gap-4">
                  {visibleSavedItems.map((item) => (
                    <ProductCard
                      key={item.id}
                      item={item}
                      onAddToCart={handleAddToCart}
                      buyerLocation={buyerLocation}
                      fallbackBuyerZip={buyerDistanceFallbackZip}
                      isSaved={savedSet.has(item.id)}
                      onToggleSave={toggleSave}
                      cartQty={cart.find((entry) => entry.itemId === item.id)?.qty ?? 0}
                    />
                  ))}
                </div>
              )}

              {!isLoadingForYou && forYouItems.length > 0 && (
                <div className="mt-10">
                  <div className="mb-4">
                    <h2 className="text-xl font-bold tracking-tight">For You</h2>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Based on items you've liked
                    </p>
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:gap-4">
                    {forYouItems.map((item) => (
                      <ProductCard
                        key={item.id}
                        item={item}
                        onAddToCart={handleAddToCart}
                        buyerLocation={buyerLocation}
                        fallbackBuyerZip={buyerDistanceFallbackZip}
                        isSaved={savedSet.has(item.id)}
                        onToggleSave={toggleSave}
                        cartQty={cart.find((entry) => entry.itemId === item.id)?.qty ?? 0}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {!activeLoading && browseMode === "producers" && sortedProducers.length > 0 && (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {sortedProducers.map((producer) => {
                const profileName =
                  stripDemoMarker(
                    producer.displayName ||
                      [producer.firstName, producer.lastName].filter(Boolean).join(" "),
                  ) || "Local Producer";

                const producerTypeLabel = prettyLabel(producer.sellerType);
                const isFeatured = producer.featuredUntil ? new Date(producer.featuredUntil).getTime() > Date.now() : false;
                const roleLabel = prettyLabel(producer.roles);
                const producerFulfillmentMethods =
                  producerFulfillmentMethodsById.get(producer.id) ?? new Set<FulfillmentFilter>();
                const producerReviewCount = producer.reviewCount ?? 0;
                const producerRatingLabel =
                  producerReviewCount > 0
                    ? `★ ${(producer.averageRating ?? 0).toFixed(1)} (${producerReviewCount})`
                    : null;
                const producerResolvedLocation = getResolvedProducerLocation(producer);

                const producerDistanceLabel = formatDistanceLabel({
                  buyerLocation,
                  producerLocation: producerResolvedLocation,
                  fallbackBuyerZip: buyerDistanceFallbackZip,
                  fallbackProducerZip: producer.zipCode,
                });
                const producerAvailabilityLabel = formatAvailabilityLabel(
                  producerPrimaryOptionById.get(producer.id) ?? null
                );

                return (
                  <Link
                    key={producer.id}
                    href={`/u/${producer.id}?back=${encodeURIComponent(
                      `${location}${typeof window !== "undefined" ? window.location.search : "?browse=producers"}`
                    )}`}
                  >
                    <Card className="overflow-hidden border-primary/15 transition hover:-translate-y-0.5 hover:shadow-md">
                      <div className="flex min-h-[188px] flex-row">
                        <div className="relative w-[34%] min-w-[122px] max-w-[168px] flex-shrink-0 overflow-hidden bg-brand-gradient-soft sm:w-[30%] sm:min-w-[136px]">
                          {producer.coverImageUrl ? (
                            <img
                              src={producer.coverImageUrl}
                              alt={`${profileName} cover`}
                              loading="lazy"
                              decoding="async"
                              className="h-full w-full object-cover"
                            />
                          ) : null}

                          <div className="absolute left-3 top-3 h-14 w-14 overflow-hidden rounded-full border-4 border-background bg-muted shadow sm:h-16 sm:w-16">
                            {producer.profileImageUrl ? (
                              <img
                                src={producer.profileImageUrl}
                                alt={profileName}
                                loading="lazy"
                                decoding="async"
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center bg-muted text-lg font-semibold text-muted-foreground">
                                {profileName.slice(0, 1).toUpperCase()}
                              </div>
                            )}
                          </div>

                          <Button
                            type="button"
                            variant={followingSet.has(producer.id) ? "default" : "secondary"}
                            size="sm"
                            className="absolute bottom-2 right-2 z-10 h-7 rounded-full px-3 text-[10px] font-semibold shadow-md"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              toggleFollow(producer.id);
                            }}
                          >
                            {followingSet.has(producer.id) ? "Following" : "Follow"}
                          </Button>
                        </div>

                        <CardContent className="flex min-w-0 flex-1 flex-col justify-start p-2.5 sm:p-3">
                          <div className="space-y-1.5">
                            <div className="space-y-1.5">
                              <h2 className="line-clamp-2 text-lg font-semibold leading-tight sm:text-[1.35rem]">
                                {profileName}
                              </h2>

                              <div className="flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground">
                                {isFeatured && (
                                  <span className="inline-flex items-center rounded-full border border-amber-300/40 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700 dark:border-amber-600/30 dark:bg-amber-950/40 dark:text-amber-400">
                                    ★ Featured
                                  </span>
                                )}
                                {producerTypeLabel && (
                                  <span className="inline-flex items-center rounded-full border border-primary/20 bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-foreground">
                                    <Sprout className="mr-1 h-3.5 w-3.5 text-primary" />
                                    {producerTypeLabel}
                                  </span>
                                )}
                              </div>

                              <div className="flex flex-wrap items-center gap-x-1 gap-y-0 text-[10px] leading-4 text-muted-foreground">
                                {producerRatingLabel ? (
                                  <span className="font-medium text-amber-600">{producerRatingLabel}</span>
                                ) : (
                                  <span className="inline-flex items-center rounded-full border border-primary/25 bg-primary/10 px-1.5 py-0 text-[9px] font-semibold uppercase tracking-wide text-primary">
                                    New
                                  </span>
                                )}
                                {producerDistanceLabel && <span>{producerDistanceLabel}</span>}
                                {producerAvailabilityLabel && <span>• {producerAvailabilityLabel}</span>}
                              </div>
                            </div>
                          </div>

                          <div className="pt-2">
                            {producerFulfillmentMethods.size > 0 && (
                              <div className="flex flex-wrap gap-2">
                                {producerFulfillmentMethods.has("seller_pickup") && (
                                  <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/15 px-2.5 py-1 text-[11px] font-medium text-foreground shadow-[0_1px_0_rgba(255,255,255,0.45)_inset]">
                                    <ShoppingBasket className="h-3.5 w-3.5 text-primary" />
                                    Pickup
                                  </span>
                                )}
                                {producerFulfillmentMethods.has("drop_zone_pickup") && (
                                  <span className="inline-flex items-center gap-1.5 rounded-full border border-sky-200/70 bg-sky-50 px-2.5 py-1 text-[11px] font-medium text-slate-700 shadow-[0_1px_0_rgba(255,255,255,0.55)_inset]">
                                    <Store className="h-3.5 w-3.5 text-sky-600" />
                                    Hub
                                  </span>
                                )}
                                {producerFulfillmentMethods.has("local_delivery") && (
                                  <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200/80 bg-amber-50 px-2.5 py-1 text-[11px] font-medium text-amber-800 shadow-[0_1px_0_rgba(255,255,255,0.55)_inset]">
                                    <Truck className="h-3.5 w-3.5 text-amber-600" />
                                    Delivery
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </div>
                    </Card>
                  </Link>
                );
              })}
            </div>
          )}

          {!activeLoading && activeError && activeCount === 0 && (
            <div className="text-center py-20">
              <h3 className="text-xl font-semibold mb-2">Couldn't load this</h3>
              <p className="text-muted-foreground mb-6">
                {typeof navigator !== "undefined" && navigator.onLine === false
                  ? "You're offline. This will fill back in once you have a connection."
                  : "Something went wrong reaching Stoop. Check your connection and try again."}
              </p>
              <Button variant="outline" onClick={() => refetchActive()} data-testid="button-retry-browse">
                Try again
              </Button>
            </div>
          )}

          {!activeLoading && !activeError && ((showBoxesResults && sortedBoxes.length === 0) || (showProductResults && sortedItems.length === 0) || (browseMode === "producers" && sortedProducers.length === 0)) && (
            <div className="text-center py-20">
              <h3 className="text-xl font-semibold mb-2">
                {showBoxesResults ? "No boxes found" : showProductResults ? "No items found" : "No growers or makers found"}
              </h3>
              <p className="text-muted-foreground mb-6">
                {showBoxesResults
                  ? searchQuery
                    ? `No boxes matched "${searchQuery}"`
                    : boxesDeliveryOnly
                      ? "No delivery-eligible boxes available for this view yet"
                      : "No boxes available in this ZIP code yet"
                  : showProductResults
                    ? searchQuery
                      ? `No results for "${searchQuery}"`
                      : selectedCategory
                        ? `No ${selectedCategory} items near you yet`
                        : fulfillmentFilter !== "any"
                          ? "No items match this fulfillment filter near you yet"
                          : "No items available near you yet"
                    : searchQuery
                      ? `No growers or makers matched "${searchQuery}"`
                      : "No growers or makers available in this ZIP code yet"}
              </p>

              {(searchQuery || activeSharedFilterCount > 0 || boxesDeliveryOnly || producerTypeFilter !== "any") && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearchQuery("");
                    setBoxesDeliveryOnly(false);
                    setProducerTypeFilter("any");
                    clearSharedFilters();
                  }}
                  data-testid="button-clear-filters"
                >
                  Clear Filters
                </Button>
              )}

              {/* An empty product search is the clearest signal we ever get
                  about what the marketplace is missing, and the shopper is
                  right there. Capture it instead of ending on a dead end. */}
              {showProductResults && searchQuery.trim() !== "" && (
                <RequestItemCard query={searchQuery.trim()} zipCode={zipCode} />
              )}
            </div>
          )}
        </div>
      </main>

      <Dialog open={Boolean(selectedBox)} onOpenChange={(open) => !open && setSelectedBox(null)}>
        <DialogContent className="max-w-2xl p-0 overflow-hidden">
          {selectedBox ? (
            <div className="flex max-h-[85vh] flex-col">
              <DialogHeader className="border-b px-5 py-4 text-left">
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center rounded-full border border-primary/20 bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-foreground">
                    {selectedBox.boxType === "single_seller" ? "Single Seller" : "Nearby Pair"}
                  </span>

                  {selectedBox.deliveryAvailable && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-amber-200/80 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                      <Truck className="h-3.5 w-3.5 text-amber-600" />
                      Delivery Available
                    </span>
                  )}

                  {selectedBox.pickupAvailable && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-primary/20 bg-primary/15 px-2 py-0.5 text-[11px] font-medium text-foreground">
                      <ShoppingBasket className="h-3.5 w-3.5 text-primary" />
                      Pickup
                    </span>
                  )}
                </div>

                <DialogTitle className="text-xl">{selectedBox.title}</DialogTitle>
                <DialogDescription className="pt-1">
                  <span className="flex flex-wrap items-center gap-1.5">
                    {selectedBox.sellerNames.map((sellerName, index) => {
                      const sellerId = selectedBox.sellerIds[index];
                      const sellerHref = sellerId
                        ? `/u/${sellerId}?back=${encodeURIComponent(location + currentSearch)}`
                        : null;
                      const displaySellerName = stripDemoMarker(sellerName) || sellerName;

                      return (
                        <span key={`${sellerId || sellerName}-${index}`} className="inline-flex items-center gap-1.5">
                          {sellerHref ? (
                            <Link href={sellerHref}>
                              <span className="cursor-pointer font-medium text-primary underline-offset-2 hover:underline">
                                {displaySellerName}
                              </span>
                            </Link>
                          ) : (
                            <span>{displaySellerName}</span>
                          )}
                          {index < selectedBox.sellerNames.length - 1 ? <span>+</span> : null}
                        </span>
                      );
                    })}
                  </span>
                </DialogDescription>

                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  <span>{selectedBox.itemCount} items</span>
                  <span>${selectedBox.subtotal.toFixed(2)}</span>
                  {selectedBox.distanceMiles != null && <span>{selectedBox.distanceMiles.toFixed(1)} mi apart</span>}
                  {selectedBox.deliveryMinimum != null && <span>Delivery min ${selectedBox.deliveryMinimum.toFixed(2)}</span>}
                  {selectedBox.deliveryFee != null && <span>Delivery fee ${selectedBox.deliveryFee.toFixed(2)}</span>}
                </div>
              </DialogHeader>

              <div className="px-5 py-4">
                <h4 className="mb-3 text-sm font-semibold">Included items</h4>

                <ScrollArea className="h-[340px] rounded-xl border bg-muted/20">
                  <div className="space-y-3 p-3">
                    {selectedBox.items.map((item) => {
                      const sourceItem = items.find((entry) => entry.id === item.id);
                      const sellerLabel =
                        selectedBox.boxType === "nearby_pair"
                          ? selectedBox.sellerNames[
                              Math.max(
                                0,
                                selectedBox.sellerIds.findIndex((sellerId) => sellerId === item.sellerId),
                              )
                            ] || "Local Seller"
                          : null;

                      const itemHref = `/items/${item.id}?back=${encodeURIComponent(location + currentSearch)}`;

                      return (
                        <Link key={item.id} href={itemHref}>
                          <div
                            className="flex cursor-pointer items-center gap-3 rounded-2xl border bg-card p-3 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                            data-testid={`box-item-link-${item.id}`}
                          >
                            {getListingImages(sourceItem)[0] ? (
                              <img
                                src={getListingImages(sourceItem)[0]}
                                alt={stripDemoMarker(item.name) || item.name}
                                loading="lazy"
                                decoding="async"
                                className="h-16 w-16 rounded-xl object-cover"
                              />
                            ) : (
                              <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl">
                                <ListingPhotoPlaceholder category={sourceItem?.category} size="xs" />
                              </div>
                            )}

                            <div className="min-w-0 flex-1">
                              <div className="line-clamp-2 text-sm font-semibold">{stripDemoMarker(item.name) || item.name}</div>
                              {sellerLabel ? (
                                <div className="mt-1 text-xs text-muted-foreground">{sellerLabel}</div>
                              ) : null}

                              {sourceItem?.freshnessNote ? (
                                <div className="mt-1 text-xs text-muted-foreground">
                                  {sourceItem.freshnessNote}
                                </div>
                              ) : null}
                            </div>

                            <div className="shrink-0 text-sm font-medium text-primary">
                              ${Number(item.price).toFixed(2)}
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </ScrollArea>

                <div className="mt-4 rounded-xl bg-muted/40 p-3 text-sm text-muted-foreground">
                  {selectedBox.deliveryAvailable ? (
                    <>
                      <div>This box is eligible for scheduled delivery where available.</div>
                      {selectedBox.deliveryMinimum != null ? (
                        <div className="mt-1">Minimum subtotal for delivery: ${selectedBox.deliveryMinimum.toFixed(2)}</div>
                      ) : null}
                    </>
                  ) : (
                    <div>This box currently falls back to pickup.</div>
                  )}
                </div>

                <div className="mt-4 flex justify-end">
                  <Button
                    type="button"
                    onClick={() => {
                      const cartItemIds = new Set(cart.map((entry) => entry.itemId));
                      const missingItems = selectedBox.items.filter((item) => !cartItemIds.has(item.id));

                      if (missingItems.length === 0) {
                        toast({
                          title: "Box already in cart",
                          description: "All items from this box are already in your cart.",
                        });
                        setSelectedBox(null);
                        return;
                      }

                      missingItems.forEach((item) => addToCart(item.id, 1));

                      toast({
                        title: "Box added to cart",
                        description: `Added ${missingItems.length} new item${missingItems.length !== 1 ? "s" : ""} from ${selectedBox.title}`,
                      });

                      setSelectedBox(null);
                    }}
                    data-testid="button-add-box-to-cart"
                  >
                    Add Box to Cart
                  </Button>
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {!embedded && (
        <ShoppingCart
          isOpen={isCartOpen}
          onClose={() => setIsCartOpen(false)}
          items={cartItemsDetails}
          onUpdateQuantity={updateQuantity}
          onRemove={removeFromCart}
          onClearCart={clearCart}
          onCheckout={handleCheckout}
        />
      )}

    </div>
  );
}
